/**
 * Console — Phase F MVP LIVE 2026-05-05
 *
 * Activation gates (all 5 PASSED):
 *   1. ANTHROPIC_API_KEY committed ✅
 *   2. AI Gateway live ✅
 *   3. Vectorize index growtify-memory populated (143 entries) ✅
 *   4. governance.search + governance.load MCP tools live ✅
 *   5. 4-block prompt cache verified ≥80% Sonnet cache hit rate (97.5% measured) ✅
 *
 * CEO directive: terminal-style (Claude Code CLI mantığı), NOT a chat tab in
 * dashboard. Customer-facing surface (PRIMARY = dashboard, SECONDARY = chat per
 * ADR-20260505-013). Tier-dependent default: managed → Pano default, self_serve
 * → Sohbet default per ADR-20260505-015.
 *
 * Stack: React + Tailwind (monospace + dark) — no shadcn yet (Phase E1 polish).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

interface Conversation {
  id: string;
  title: string | null;
  default_model: string | null;
  created_at: string;
  updated_at: string;
}

interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  model?: string;
  tokens_input?: number;
  tokens_output?: number;
  cost_micro_usd?: number;
  created_at: string;
}

interface TenantOption {
  slug: string;
  type?: string;
  tenant_type?: string;  // sometimes returned as 'tenant_type' (view) vs 'type' (raw row)
  vertical?: string;
  service_tier?: string;
  monthly_credit_grant?: number;
  credit_balance?: number;
}

interface CreditBalance {
  balance: number;
  monthly_grant: number;
  last_reset_at: string | null;
  lifetime_consumed: number;
}

interface PendingToolAction {
  id: string;
  conversation_id: string;
  tenant_id: string;
  tool_name: string;
  tool_args: any;
  proposed_action_summary: string;
  status: string;
  proposed_at: string;
  expires_at: string;
  decided_at?: string | null;
  decided_by?: string | null;
  executed_at?: string | null;
  execution_result?: any;
  execution_error?: string | null;
  time_remaining_minutes?: number | null;
}

const MODELS = [
  { id: "claude-opus-4-7",   label: "Opus 4.7 (en akıllı)",   provider: "anthropic" as const },
  { id: "claude-sonnet-4-6", label: "Sonnet 4.6 (denge)",     provider: "anthropic" as const },
  { id: "claude-haiku-4-5",  label: "Haiku 4.5 (hızlı/ucuz)", provider: "anthropic" as const },
  { id: "kimi-k2",           label: "Kimi K2 (uzun context)", provider: "moonshot" as const },
  { id: "gemini-2.5-pro",    label: "Gemini 2.5 Pro",         provider: "google" as const },
  { id: "gpt-5",             label: "GPT-5 (alt fikir)",      provider: "openai" as const },
];

const TASK_TYPES = [
  "everyday", "high_stakes_decision", "fast_summary", "code_heavy",
  "creative_writing", "long_context_chinese", "alt_perspective", "structured_extraction",
];

export default function Console() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [taskType, setTaskType] = useState("everyday");
  const [sending, setSending] = useState(false);
  const [totalCostMicroUsd, setTotalCostMicroUsd] = useState(0);

  // Tenant context (Phase H credit system)
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [activeTenant, setActiveTenant] = useState<string>("_global");
  const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(null);

  // Pending tool actions (Phase H Sprint 2 — state-mutating consent gate)
  const [pendingActions, setPendingActions] = useState<PendingToolAction[]>([]);
  const [decidingActionId, setDecidingActionId] = useState<string | null>(null);

  // Multi-AI provider availability (Phase D)
  const [providerStatus, setProviderStatus] = useState<Array<{ id: string; available: boolean; reason?: string }>>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load conversations + tenants on mount
  useEffect(() => {
    api<{ conversations: Conversation[] }>("/console/conversations").then((d) => {
      setConversations(d.conversations);
      if (d.conversations.length > 0) {
        loadConversation(d.conversations[0].id);
      }
    });
    api<{ by_type: Array<{ type: string; n: number }>; }>("/tenants/_summary").then(() => {
      // Fetch full list from /api/tenants for dropdown
      api<{ tenants: TenantOption[] }>("/tenants").then((d) => {
        setTenants(d.tenants ?? []);
      }).catch(() => { /* fallback to none */ });
    });
    // Provider availability (Phase D Multi-AI)
    api<{ providers: Array<{ id: string; available: boolean; reason?: string }> }>("/ai/providers")
      .then((d) => setProviderStatus(d.providers ?? []))
      .catch(() => setProviderStatus([]));
  }, []);

  // Refresh credit balance when active tenant changes
  useEffect(() => {
    if (activeTenant === "_global") {
      setCreditBalance(null);
      return;
    }
    api<CreditBalance>(`/credits/balance/${activeTenant}`)
      .then((d) => setCreditBalance(d))
      .catch(() => setCreditBalance(null));
  }, [activeTenant]);

  // Refresh pending tool actions when conversation changes (or after a stage)
  async function refreshPendingActions(convId: string | null) {
    if (!convId) {
      setPendingActions([]);
      return;
    }
    try {
      const d = await api<{ actions: PendingToolAction[] }>(`/tool-actions/by-conversation/${convId}`);
      setPendingActions(d.actions ?? []);
    } catch {
      setPendingActions([]);
    }
  }

  useEffect(() => {
    refreshPendingActions(activeConvId);
  }, [activeConvId]);

  async function decideAction(actionId: string, decision: "approve" | "reject") {
    setDecidingActionId(actionId);
    try {
      const result = await api<any>(`/tool-actions/${actionId}/decide`, {
        method: "POST",
        body: JSON.stringify({ decision }),
      });
      // Append a system message reflecting the outcome
      const outcomeMsg: Message = {
        id: `decision-${Date.now()}`,
        role: "system",
        content:
          decision === "approve"
            ? `✅ Onaylandı: ${result.execution_result?.work_item_id ?? result.execution_result?.request_id ?? actionId} (${result.status})`
            : `❌ Reddedildi: ${actionId}`,
        created_at: new Date().toISOString(),
      };
      setMessages((curr) => [...curr, outcomeMsg]);
      await refreshPendingActions(activeConvId);
    } catch (e: any) {
      const errMsg: Message = {
        id: `decision-err-${Date.now()}`,
        role: "system",
        content: `[error deciding ${actionId}] ${e?.message ?? e}`,
        created_at: new Date().toISOString(),
      };
      setMessages((curr) => [...curr, errMsg]);
    } finally {
      setDecidingActionId(null);
    }
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function loadConversation(id: string) {
    setActiveConvId(id);
    const data = await api<{ conversation: Conversation; messages: Message[] }>(`/console/conversations/${id}`);
    setMessages(data.messages);
    if (data.conversation.default_model) setModel(data.conversation.default_model);
    const total = data.messages.reduce((sum, m) => sum + (m.cost_micro_usd ?? 0), 0);
    setTotalCostMicroUsd(total);
  }

  async function newConversation() {
    const id = `conv-${Date.now()}`;
    await api(`/console/conversations`, {
      method: "POST",
      body: JSON.stringify({ id, title: "New conversation", default_model: model }),
    });
    setConversations([{ id, title: "New conversation", default_model: model, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }, ...conversations]);
    setActiveConvId(id);
    setMessages([]);
    setTotalCostMicroUsd(0);
  }

  async function sendMessage() {
    if (!input.trim() || sending) return;

    let convId = activeConvId;
    if (!convId) {
      convId = `conv-${Date.now()}`;
      await api(`/console/conversations`, {
        method: "POST",
        body: JSON.stringify({ id: convId, title: input.substring(0, 50), default_model: model }),
      });
      setConversations([{ id: convId, title: input.substring(0, 50), default_model: model, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }, ...conversations]);
      setActiveConvId(convId);
    }

    const userMsg: Message = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: input,
      created_at: new Date().toISOString(),
    };
    setMessages([...messages, userMsg]);
    const messageContent = input;
    setInput("");
    setSending(true);

    // Map UI taskType → AI Gateway stakes_override
    const stakesMap: Record<string, "low" | "medium" | "high" | "owner"> = {
      everyday: "low",
      fast_summary: "low",
      structured_extraction: "low",
      creative_writing: "medium",
      code_heavy: "medium",
      alt_perspective: "medium",
      long_context_chinese: "medium",
      high_stakes_decision: "high",
    };

    // Streaming-with-tools (Phase H Sprint 2): both streaming UX AND tool capability.
    // SSE events: meta → text deltas (progressive) ↔ tool_call (after execution) → usage → done
    const placeholderId = `streaming-${Date.now()}`;
    const placeholder: Message = {
      id: placeholderId,
      role: "assistant",
      content: "",
      created_at: new Date().toISOString(),
    };
    setMessages([...messages, userMsg, placeholder]);

    try {
      const apiBase = (import.meta as any).env?.VITE_API_BASE || "/api";
      const resp = await fetch(`${apiBase}/console/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversation_id: convId,
          message: messageContent,
          agent_name: "env-cto-agent",
          tenant_id: activeTenant,
          stakes_override: stakesMap[taskType] ?? "medium",
          max_tokens: 2048,
          model_override: model && !model.startsWith("claude-") ? model : undefined,
        }),
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`stream open failed (${resp.status})`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accumulatedText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split("\n\n");
        buffer = events.pop() ?? "";

        for (const evt of events) {
          if (!evt.trim()) continue;
          const lines = evt.split("\n");
          let eventType = "";
          let dataStr = "";
          for (const line of lines) {
            if (line.startsWith("event:")) eventType = line.slice(6).trim();
            else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          }
          if (!dataStr) continue;
          let parsed: any;
          try { parsed = JSON.parse(dataStr); } catch { continue; }

          if (eventType === "meta" && parsed.compression?.compressed) {
            // L2 conversation compression banner — inserted BEFORE placeholder
            const c = parsed.compression;
            const sourceTag = c.summary_source === "cache" ? "cached" : "fresh";
            const compMsg: Message = {
              id: `compression-${Date.now()}`,
              role: "system",
              content: `📚 L2 compression: ${c.prefix_summarized} prior msg${c.prefix_summarized === 1 ? "" : "s"} → summary (${sourceTag}); kept last ${c.recent_kept} verbatim`,
              created_at: new Date().toISOString(),
            };
            setMessages((curr) => {
              const placeholderIdx = curr.findIndex((m) => m.id === placeholderId);
              if (placeholderIdx === -1) return [...curr, compMsg];
              return [
                ...curr.slice(0, placeholderIdx),
                compMsg,
                ...curr.slice(placeholderIdx),
              ];
            });
          } else if (eventType === "text" && parsed.delta) {
            accumulatedText += parsed.delta;
            setMessages((curr) =>
              curr.map((m) =>
                m.id === placeholderId ? { ...m, content: accumulatedText } : m
              )
            );
          } else if (eventType === "tool_call") {
            // Insert a system message BEFORE the streaming placeholder summarizing the tool call
            const inputPreview = JSON.stringify(parsed.input).slice(0, 80);
            const resultOk = parsed.result?.ok;
            const truncated = parsed.result?._truncated || parsed.result?._body_truncated;
            const truncatedTag = truncated ? " ⚠ truncated" : "";
            const resultPreview = resultOk
              ? `→ ${parsed.result.count !== undefined ? `${parsed.result.count} results` : "ok"}${truncatedTag}`
              : `→ error: ${parsed.result?.error ?? "?"}`;
            // Per-tool credit weight (matches backend READ_ONLY_TOOLS table)
            const TOOL_WEIGHTS: Record<string, number> = {
              governance_load: 3,
              governance_search: 2,
              get_today_summary: 2,
            };
            const weight = TOOL_WEIGHTS[parsed.name] ?? 1;
            const toolMsg: Message = {
              id: `tool-${Date.now()}-${parsed.turn}`,
              role: "system",
              content: `🔧 turn ${parsed.turn}: ${parsed.name}(${inputPreview}) ${resultPreview}  [${weight} kredi]`,
              created_at: new Date().toISOString(),
            };
            // Insert before placeholder
            setMessages((curr) => {
              const placeholderIdx = curr.findIndex((m) => m.id === placeholderId);
              if (placeholderIdx === -1) return [...curr, toolMsg];
              return [
                ...curr.slice(0, placeholderIdx),
                toolMsg,
                ...curr.slice(placeholderIdx),
              ];
            });
          } else if (eventType === "usage") {
            setMessages((curr) =>
              curr.map((m) =>
                m.id === placeholderId
                  ? {
                      ...m,
                      content: accumulatedText,
                      tokens_input: parsed.usage?.input_tokens ?? 0,
                      tokens_output: parsed.usage?.output_tokens ?? 0,
                      cost_micro_usd: parsed.cost_micro_usd ?? 0,
                    }
                  : m
              )
            );
            setTotalCostMicroUsd((prev) => prev + (parsed.cost_micro_usd ?? 0));
          } else if (eventType === "error") {
            throw new Error(parsed.error ?? "stream error");
          }
        }
      }

      // Refresh credit balance after chat
      if (activeTenant !== "_global") {
        api<CreditBalance>(`/credits/balance/${activeTenant}`).then(setCreditBalance).catch(() => {});
      }
      // Refresh pending tool actions in case the model staged any
      refreshPendingActions(activeConvId);
    } catch (e: any) {
      setMessages((curr) =>
        curr.map((m) =>
          m.id === placeholderId
            ? { ...m, role: "system" as const, content: `[error] ${e.message}` }
            : m
        )
      );
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <div className="font-mono text-sm bg-gray-900 text-gray-100 min-h-screen flex">
      {/* Sidebar — conversations */}
      <aside className="w-64 border-r border-gray-700 bg-gray-950 flex flex-col">
        <div className="p-3 border-b border-gray-700">
          <button
            onClick={newConversation}
            className="w-full px-3 py-2 bg-growtify-500 text-white rounded text-xs hover:bg-growtify-900"
          >
            + New conversation
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => loadConversation(c.id)}
              className={`w-full text-left px-3 py-2 text-xs border-b border-gray-800 truncate ${
                activeConvId === c.id ? "bg-gray-800 text-white" : "text-gray-400 hover:bg-gray-900"
              }`}
            >
              {c.title ?? c.id}
            </button>
          ))}
        </div>
      </aside>

      {/* Main chat area */}
      <main className="flex-1 flex flex-col">
        {/* Top bar — tenant + model + task + cost + credit balance */}
        <div className="flex items-center justify-between border-b border-gray-700 bg-gray-950 px-4 py-2 text-xs">
          <div className="flex gap-3 items-center flex-wrap">
            <span className="text-gray-500">Tenant:</span>
            <select
              value={activeTenant}
              onChange={(e) => setActiveTenant(e.target.value)}
              className="bg-gray-800 text-gray-100 border border-gray-700 rounded px-2 py-1 text-xs"
              title="Tenant context — chat usage o tenant'ın kredisinden düşülür"
            >
              <option value="_global">_global (master, no credit)</option>
              {tenants.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.slug} ({t.tenant_type ? t.tenant_type.charAt(0) : "?"}/{t.service_tier ?? "?"})
                </option>
              ))}
            </select>
            <span className="text-gray-500 ml-3">Task:</span>
            <select
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              className="bg-gray-800 text-gray-100 border border-gray-700 rounded px-2 py-1 text-xs"
            >
              {TASK_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <span className="text-gray-500 ml-3">Model hint:</span>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="bg-gray-800 text-gray-100 border border-gray-700 rounded px-2 py-1 text-xs"
              title="Anthropic = full features (tools, cache); diğerleri = simple text"
            >
              {MODELS.map((m) => {
                const ps = providerStatus.find((p) => p.id === m.provider);
                const available = ps?.available ?? m.provider === "anthropic";
                return (
                  <option key={m.id} value={m.id} disabled={!available}>
                    {available ? "" : "🔒 "}{m.label}{!available ? " (key gerekli)" : ""}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="flex gap-4 items-center">
            {/* Credit balance badge */}
            {creditBalance && creditBalance.monthly_grant > 0 && (
              <div className="text-gray-400">
                <span className="text-gray-500">Kredi: </span>
                <span className={creditBalance.balance < 50 ? "text-yellow-400" : "text-growtify-500"}>
                  {creditBalance.balance}
                </span>
                <span className="text-gray-600"> / {creditBalance.monthly_grant}</span>
                {creditBalance.balance === 0 && (
                  <span className="ml-2 text-red-400">⚠ kredi bitti</span>
                )}
              </div>
            )}
            {activeTenant === "_global" && (
              <div className="text-gray-500" title="_global tenant credit-exempt (master access)">
                <span className="text-gray-600">⚪ exempt</span>
              </div>
            )}
            <div className="text-gray-500">
              Session: <span className="text-growtify-500">${(totalCostMicroUsd / 1_000_000).toFixed(4)}</span>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && (
            <div className="text-gray-500 text-center py-12">
              Hi Hüseyin — Growtify env console.<br />
              14 MCP tool, 16 D1 table, 100% test pass.<br />
              Type a message + Cmd+Enter (or Ctrl+Enter) to send.
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`whitespace-pre-wrap ${m.role === "user" ? "text-growtify-500" : m.role === "system" ? "text-yellow-400 text-xs" : "text-gray-100"}`}>
              <span className="text-gray-500 select-none">
                {m.role === "user" ? "huseyin@growtify $ " : m.role === "system" ? "system: " : `${m.model ?? "?"}: `}
              </span>
              {m.content}
              {m.role === "assistant" && (m.tokens_input || m.tokens_output) ? (
                <span className="block text-xs text-gray-600 mt-1">
                  {m.tokens_input ?? 0}→{m.tokens_output ?? 0} tokens · ${((m.cost_micro_usd ?? 0) / 1_000_000).toFixed(4)}
                </span>
              ) : null}
            </div>
          ))}
          {sending && <div className="text-gray-500">[sending...]</div>}
          <div ref={messagesEndRef} />
        </div>

        {/* Pending tool actions panel — Phase H Sprint 2 consent gate */}
        {pendingActions.length > 0 && (
          <div className="border-t border-yellow-700 bg-yellow-950 px-3 py-2 max-h-64 overflow-y-auto">
            <div className="text-xs text-yellow-300 font-bold mb-1">
              ⚠ {pendingActions.filter((a) => a.status === "pending").length} pending eylem
              {" · "}
              <span className="text-gray-400 font-normal">{pendingActions.length} total in this conversation</span>
            </div>
            {pendingActions.map((a) => {
              const isPending = a.status === "pending";
              const tag =
                a.status === "executed" ? "✅ executed" :
                a.status === "rejected" ? "❌ rejected" :
                a.status === "expired" ? "⌛ expired" :
                a.status === "failed" ? "💥 failed" :
                a.status === "pending" ? `⏳ pending (${a.time_remaining_minutes ?? "?"}m left)` :
                a.status;
              return (
                <div key={a.id} className="text-xs border-l-2 border-yellow-600 pl-2 py-1 mb-1">
                  <div className="text-yellow-200">
                    <span className="text-gray-500 font-mono">[{a.tool_name}]</span>{" "}
                    {a.proposed_action_summary}
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-gray-500">{tag} · {a.id}</span>
                    {isPending && (
                      <span>
                        <button
                          onClick={() => decideAction(a.id, "approve")}
                          disabled={decidingActionId !== null}
                          className="px-2 py-0.5 bg-green-700 hover:bg-green-600 text-white rounded text-xs mr-1 disabled:opacity-50"
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => decideAction(a.id, "reject")}
                          disabled={decidingActionId !== null}
                          className="px-2 py-0.5 bg-red-700 hover:bg-red-600 text-white rounded text-xs disabled:opacity-50"
                        >
                          ✕ Reject
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Input */}
        <div className="border-t border-gray-700 bg-gray-950 p-3">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Mesajını yaz... (Cmd+Enter ile gönder)"
            className="w-full bg-gray-800 text-gray-100 border border-gray-700 rounded px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:border-growtify-500"
            rows={3}
            disabled={sending}
          />
          <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
            <span>{input.length} chars · ~{Math.ceil(input.length / 4)} tokens</span>
            <button
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              className="px-4 py-1 bg-growtify-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Send (⌘↵)
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
